import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clipboard,
  ExternalLink,
  ListVideo,
  Loader2,
  Plus,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { Input } from '../../ui/input';

const POLL_INTERVAL_MS = 3_000;
const POLL_TIMEOUT_MS = 15 * 60 * 1_000;
const TERMINAL_STATUSES = new Set(['complete', 'failed']);
const RUNNING_STATUSES = new Set(['queued', 'fetching', 'analyzing', 'synthesizing']);

const asArray = (value) => (Array.isArray(value) ? value : []);

const createMergeRequestId = () => {
  const requestId = globalThis.crypto?.randomUUID?.();
  if (!requestId) {
    throw new Error('This browser could not create a merge request ID. Refresh and try again.');
  }
  return requestId;
};

const formatDateTime = (value) => {
  if (!value) return 'Unknown date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const lensClasses = {
  messaging: 'border-sky-500/20 bg-sky-500/10 text-sky-700 dark:text-sky-300',
  content: 'border-fuchsia-500/20 bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300',
  product: 'border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  growth: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
};

const evidenceMixText = (report) => {
  const mix = report?.evidence_mix || {};
  const transcript = Number(mix.transcript || 0);
  const comments = Number(mix.comments || 0);
  const metadata = Number(mix.metadata || 0);
  return `${transcript} transcript${transcript === 1 ? '' : 's'}, `
    + `${comments} comments, ${metadata} metadata.`;
};

const reportTypeLabel = (report) => {
  if (report?.is_merged === true) return 'Combined report';
  const batchIndex = Number(report?.batch_index);
  return Number.isInteger(batchIndex) && batchIndex >= 0
    ? `Batch ${batchIndex + 1}`
    : 'Playlist batch';
};

const reportWindowLabel = (report) => {
  if (report?.is_merged === true) return `${Number(report?.video_count || 0)} videos combined`;
  const start = Number(report?.batch_start_position);
  const count = Number(report?.video_count);
  if (!Number.isFinite(start) || !Number.isFinite(count) || count <= 0) return null;
  return `Videos ${start + 1}–${start + count}`;
};

const buildIdeaBrief = (idea) => {
  const evidenceLines = asArray(idea?.evidence).map((item) => (
    `- [${item.video_title || 'Source video'}](https://www.youtube.com/watch?v=${item.video_id})`
    + ` — ${item.channel || 'Unknown channel'}: “${item.excerpt || ''}”`
  ));
  return [
    `# ${idea?.title || 'Playlist idea'}`,
    '',
    `**What:** ${idea?.what || ''}`,
    '',
    `**Why it matters:** ${idea?.why_it_matters || ''}`,
    '',
    `**First step:** ${idea?.first_step || ''}`,
    '',
    '**Evidence:**',
    ...(evidenceLines.length ? evidenceLines : ['- No attributed evidence was returned.']),
  ].join('\n');
};

const reportLabel = (report) => (
  report?.label
  || report?.playlist_title
  || report?.playlist_id
  || 'YouTube playlist'
);

function EvidenceList({ evidence }) {
  const items = asArray(evidence);
  if (!items.length) return null;
  return (
    <div className="mt-4 space-y-2">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        Evidence
      </p>
      {items.map((item, index) => (
        <a
          className="block rounded-xl border border-slate-200 bg-slate-50/80 p-3 text-sm leading-6 text-slate-700 transition hover:border-fuchsia-300 hover:bg-fuchsia-50/60 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-300 dark:hover:border-fuchsia-400/30 dark:hover:bg-fuchsia-400/[0.08]"
          href={`https://www.youtube.com/watch?v=${item.video_id}`}
          key={`${item.video_id}-${index}`}
          rel="noreferrer"
          target="_blank"
        >
          <span className="font-semibold text-slate-900 dark:text-white">
            {item.video_title || 'Source video'}
          </span>
          <span className="ml-1 text-slate-500 dark:text-slate-400">
            · {item.channel || 'Unknown channel'}
          </span>
          <span className="mt-1 block">
            “{item.excerpt}”
            <ExternalLink className="ml-1 inline h-3.5 w-3.5" />
          </span>
        </a>
      ))}
    </div>
  );
}

function IdeaActions({ idea, onAddToQueue, onCopyBrief }) {
  return (
    <div className="mt-5 flex flex-wrap gap-2">
      {idea?.lens === 'content' ? (
        <Button
          className="bg-fuchsia-600 text-white hover:bg-fuchsia-700 dark:bg-fuchsia-500 dark:text-white dark:hover:bg-fuchsia-400"
          onClick={() => onAddToQueue(idea.title)}
          type="button"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add to Queue
        </Button>
      ) : null}
      <Button
        className="border-slate-200 text-slate-700 hover:bg-slate-100 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/[0.06]"
        onClick={() => onCopyBrief(idea)}
        type="button"
        variant="outline"
      >
        <Clipboard className="mr-2 h-4 w-4" />
        Copy brief
      </Button>
    </div>
  );
}

function IdeaCard({
  idea,
  rank,
  hero = false,
  onAddToQueue,
  onCopyBrief,
}) {
  return (
    <Card className={`border-slate-200 bg-white/90 p-6 dark:border-white/10 dark:bg-white/[0.03] ${hero ? 'shadow-sm' : ''}`}>
      <div className="flex flex-wrap items-center gap-2">
        {rank ? (
          <Badge className="border-fuchsia-500/20 bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300">
            #{rank}
          </Badge>
        ) : null}
        <Badge className={lensClasses[idea?.lens] || 'border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300'}>
          {idea?.lens || 'idea'}
        </Badge>
        <Badge className="border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300">
          {Number(idea?.composite || 0).toFixed(1)} composite
        </Badge>
        {idea?.derived_from_idea_id ? (
          <Badge className="border-violet-500/20 bg-violet-500/10 text-violet-700 dark:text-violet-300">
            Counter-angle
          </Badge>
        ) : null}
        {idea?.brand_flag ? (
          <Badge className="border-rose-500/20 bg-rose-500/10 text-rose-700 dark:text-rose-300">
            Brand conflict
          </Badge>
        ) : null}
      </div>

      <h3 className={`${hero ? 'text-xl' : 'text-lg'} mt-4 font-semibold leading-8 text-slate-900 dark:text-white`}>
        {idea?.title}
      </h3>
      <p className="mt-3 text-sm leading-6 text-slate-700 dark:text-slate-300">
        {idea?.what}
      </p>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 dark:border-white/10 dark:bg-white/[0.04]">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            Why it matters
          </p>
          <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-300">
            {idea?.why_it_matters}
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 dark:border-white/10 dark:bg-white/[0.04]">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            First step
          </p>
          <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-300">
            {idea?.first_step}
          </p>
        </div>
      </div>
      {idea?.brand_flag_reason ? (
        <p className="mt-4 rounded-xl border border-rose-200 bg-rose-50/80 p-3 text-sm leading-6 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
          {idea.brand_flag_reason}
        </p>
      ) : null}
      <EvidenceList evidence={idea?.evidence} />
      <IdeaActions
        idea={idea}
        onAddToQueue={onAddToQueue}
        onCopyBrief={onCopyBrief}
      />
    </Card>
  );
}

export default function MarketingPlaylistTab({ adminFetch, onAddToQueue }) {
  const [playlistUrl, setPlaylistUrl] = useState('');
  const [label, setLabel] = useState('');
  const [reports, setReports] = useState([]);
  const [activeReport, setActiveReport] = useState(null);
  const [loadingReports, setLoadingReports] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const [merging, setMerging] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [reportError, setReportError] = useState('');
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [allIdeasOpen, setAllIdeasOpen] = useState(false);
  const [contraryOpen, setContraryOpen] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const pollSessionRef = useRef({ reportId: '', startedAt: 0 });
  const mergeAttemptRef = useRef({ rawReportId: '', requestId: '' });

  const mergeReportSummary = useCallback((report) => {
    if (!report?.report_id) return;
    setReports((current) => {
      const next = [
        { ...current.find((item) => item.report_id === report.report_id), ...report },
        ...current.filter((item) => item.report_id !== report.report_id),
      ];
      return next.sort((left, right) => String(right.created_at || '').localeCompare(String(left.created_at || '')));
    });
  }, []);

  const fetchReport = useCallback(async (reportId) => {
    const response = await adminFetch(`/admin/marketing/playlist/reports/${reportId}`);
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.detail || 'Failed to load this playlist report.');
    }
    setActiveReport(payload);
    mergeReportSummary(payload);
    return payload;
  }, [adminFetch, mergeReportSummary]);

  const fetchReports = useCallback(async ({ openLatest = true } = {}) => {
    setLoadingReports(true);
    setLoadError('');
    try {
      const response = await adminFetch('/admin/marketing/playlist/reports?limit=20');
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.detail || 'Failed to load playlist reports.');
      }
      const nextReports = asArray(payload?.reports);
      setReports(nextReports);
      if (openLatest && nextReports[0]?.report_id) {
        await fetchReport(nextReports[0].report_id);
      } else if (openLatest && !nextReports.length) {
        setActiveReport(null);
      }
    } catch (fetchError) {
      setLoadError(fetchError?.message || 'Failed to load playlist reports.');
    } finally {
      setLoadingReports(false);
    }
  }, [adminFetch, fetchReport]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    const reportId = activeReport?.report_id;
    const status = activeReport?.status;
    if (!reportId || TERMINAL_STATUSES.has(status) || pollTimedOut) return undefined;

    if (pollSessionRef.current.reportId !== reportId) {
      pollSessionRef.current = { reportId, startedAt: Date.now() };
      setPollTimedOut(false);
    }

    let cancelled = false;
    let timerId;

    const poll = async () => {
      if (Date.now() - pollSessionRef.current.startedAt >= POLL_TIMEOUT_MS) {
        if (!cancelled) {
          setPollTimedOut(true);
          setReportError('This run has been polling for 15 minutes. Refresh manually to check its latest status.');
        }
        return;
      }
      try {
        const next = await fetchReport(reportId);
        if (cancelled) return;
        setReportError('');
        if (TERMINAL_STATUSES.has(next?.status)) {
          await fetchReports({ openLatest: false });
          return;
        }
      } catch (pollError) {
        if (!cancelled) {
          setReportError(pollError?.message || 'Could not refresh this report. We will try again.');
        }
      }
      if (!cancelled) {
        timerId = window.setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    timerId = window.setTimeout(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [
    activeReport?.report_id,
    activeReport?.status,
    fetchReport,
    fetchReports,
    pollTimedOut,
  ]);

  const handleAnalyze = async ({ resumeFromReport = null } = {}) => {
    const sourcePlaylistUrl = resumeFromReport
      ? activeReport?.playlist_url
      : playlistUrl.trim();
    const sourceLabel = resumeFromReport
      ? activeReport?.label || null
      : label.trim() || null;
    if (!sourcePlaylistUrl) {
      setReportError('Enter a YouTube playlist URL before starting the analysis.');
      return;
    }
    setSubmitting(true);
    setContinuing(Boolean(resumeFromReport));
    setReportError('');
    setPollTimedOut(false);
    try {
      const response = await adminFetch('/admin/marketing/playlist/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          playlist_url: sourcePlaylistUrl,
          label: sourceLabel,
          ...(resumeFromReport ? { resume_from_report_id: resumeFromReport } : {}),
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.detail || 'Failed to start playlist analysis.');
      }
      toast.success('Playlist analysis started.');
      if (resumeFromReport) {
        const startedAt = Date.now();
        let continuedReport;
        while (!continuedReport) {
          if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
            throw new Error(
              'This continuation has been polling for 15 minutes. Retry from the previous report to check it again.',
            );
          }
          const detailResponse = await adminFetch(
            `/admin/marketing/playlist/reports/${payload.report_id}`,
          );
          const detail = await detailResponse.json().catch(() => ({}));
          if (!detailResponse.ok) {
            throw new Error(detail?.detail || 'Failed to check the continued playlist report.');
          }
          if (!detail?.report_id || !detail?.status) {
            throw new Error('The continued playlist report returned an incomplete status.');
          }
          mergeReportSummary(detail);
          if (TERMINAL_STATUSES.has(detail.status)) {
            continuedReport = detail;
            break;
          }
          await new Promise((resolve) => {
            window.setTimeout(resolve, POLL_INTERVAL_MS);
          });
        }
        if (continuedReport.status === 'failed') {
          throw new Error(
            continuedReport.error
            || continuedReport.detail
            || 'The next playlist batch could not be completed.',
          );
        }
        setActiveReport(continuedReport);
      } else {
        pollSessionRef.current = {
          reportId: payload.report_id,
          startedAt: Date.now(),
        };
        setActiveReport({
          report_id: payload.report_id,
          playlist_url: sourcePlaylistUrl,
          label: sourceLabel,
          status: payload.status || 'queued',
          progress: { stage: 'Queued', current: 0, total: 0 },
          video_count: 0,
        });
        await fetchReport(payload.report_id);
      }
    } catch (analyzeError) {
      const message = analyzeError?.message || 'Failed to start playlist analysis.';
      setReportError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
      setContinuing(false);
    }
  };

  const handleManualRefresh = async () => {
    if (!activeReport?.report_id) {
      await fetchReports();
      return;
    }
    setRefreshing(true);
    setReportError('');
    try {
      const next = await fetchReport(activeReport.report_id);
      if (!TERMINAL_STATUSES.has(next?.status)) {
        pollSessionRef.current = {
          reportId: next.report_id,
          startedAt: Date.now(),
        };
        setPollTimedOut(false);
      }
    } catch (refreshError) {
      setReportError(refreshError?.message || 'Failed to refresh this report.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleOpenReport = async (reportId) => {
    setRefreshing(true);
    setReportError('');
    setPollTimedOut(false);
    try {
      const next = await fetchReport(reportId);
      if (!TERMINAL_STATUSES.has(next?.status)) {
        pollSessionRef.current = { reportId, startedAt: Date.now() };
      }
      setHistoryOpen(false);
    } catch (openError) {
      setReportError(openError?.message || 'Failed to open this report.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleAddToQueue = useCallback((title) => {
    if (typeof onAddToQueue === 'function' && title) {
      onAddToQueue(title);
      toast.success('Added this YouTube master to the Content Queue.');
    }
  }, [onAddToQueue]);

  const handleCopyBrief = useCallback(async (idea) => {
    try {
      await navigator.clipboard.writeText(buildIdeaBrief(idea));
      toast.success('Idea brief copied.');
    } catch {
      toast.error('Could not copy the idea brief.');
    }
  }, []);

  const ideas = useMemo(() => asArray(activeReport?.ideas), [activeReport?.ideas]);
  const ideaById = useMemo(
    () => new Map(ideas.map((idea) => [idea.idea_id, idea])),
    [ideas],
  );
  const topIdeas = useMemo(
    () => asArray(activeReport?.top_3).map((ideaId) => ideaById.get(ideaId)).filter(Boolean),
    [activeReport?.top_3, ideaById],
  );
  const flaggedIdeas = useMemo(
    () => ideas.filter((idea) => idea?.brand_flag && idea?.counter_angle),
    [ideas],
  );
  const groupedHistoryReports = useMemo(() => {
    const groupsByPlaylist = new Map();

    reports.forEach((report) => {
      const playlistId = String(report?.playlist_id || '').trim();
      const legacyKey = [
        report?.report_id,
        report?.created_at,
        report?.playlist_title,
        report?.label,
      ].filter(Boolean).join('|') || 'unknown';
      const key = playlistId ? `playlist:${playlistId}` : `legacy:${legacyKey}`;
      const group = groupsByPlaylist.get(key) || {
        key,
        title: reportLabel(report),
        rawReports: [],
        mergedReports: [],
      };

      if (report?.is_merged === true) {
        group.mergedReports.push(report);
      } else {
        group.rawReports.push(report);
      }
      groupsByPlaylist.set(key, group);
    });

    const compareReportIds = (left, right) => String(left?.report_id || '').localeCompare(String(right?.report_id || ''));
    const compareCreatedAt = (left, right) => String(left?.created_at || '').localeCompare(String(right?.created_at || ''));
    const normalizedBatchIndex = (report) => {
      const batchIndex = Number(report?.batch_index);
      return Number.isInteger(batchIndex) && batchIndex >= 0 ? batchIndex : 0;
    };

    return Array.from(groupsByPlaylist.values())
      .map((group) => ({
        ...group,
        reports: [
          ...group.rawReports.sort((left, right) => (
            normalizedBatchIndex(left) - normalizedBatchIndex(right)
            || compareCreatedAt(left, right)
            || compareReportIds(left, right)
          )),
          ...group.mergedReports.sort((left, right) => (
            compareCreatedAt(right, left) || compareReportIds(right, left)
          )),
        ],
      }))
      .sort((left, right) => left.title.localeCompare(right.title) || left.key.localeCompare(right.key));
  }, [reports]);
  const rawReportsForActivePlaylist = reports.filter((report) => (
    report?.playlist_id === activeReport?.playlist_id
    && report?.status === 'complete'
    && report?.is_merged !== true
  ));

  const handleMerge = async () => {
    const rawReportId = rawReportsForActivePlaylist[0]?.report_id;
    if (!rawReportId) return;
    setMerging(true);
    setReportError('');
    let reachedTerminalStatus = false;
    try {
      if (
        !mergeAttemptRef.current.requestId
        || mergeAttemptRef.current.rawReportId !== rawReportId
      ) {
        mergeAttemptRef.current = {
          rawReportId,
          requestId: createMergeRequestId(),
        };
      }
      const { requestId } = mergeAttemptRef.current;
      const response = await adminFetch(`/admin/marketing/playlist/reports/${rawReportId}/merge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          merge_request_id: requestId,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.detail || 'Failed to combine playlist reports.');
      }
      if (!payload?.report_id) {
        throw new Error('The merge request did not return a report ID. Try Combine again.');
      }

      const mergeReportId = payload.report_id;
      const startedAt = Date.now();
      let mergedReport;
      while (!mergedReport) {
        if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
          throw new Error(
            'This merge has been polling for 15 minutes. Try Combine again to check the same merge request.',
          );
        }
        const detailResponse = await adminFetch(
          `/admin/marketing/playlist/reports/${mergeReportId}`,
        );
        const detail = await detailResponse.json().catch(() => ({}));
        if (!detailResponse.ok) {
          throw new Error(detail?.detail || 'Failed to check the combined playlist report.');
        }
        if (!detail?.report_id || !detail?.status) {
          throw new Error('The combined playlist report returned an incomplete status. Try Combine again.');
        }
        mergeReportSummary(detail);
        if (TERMINAL_STATUSES.has(detail.status)) {
          mergedReport = detail;
          break;
        }
        await new Promise((resolve) => {
          window.setTimeout(resolve, POLL_INTERVAL_MS);
        });
      }

      reachedTerminalStatus = true;
      if (mergedReport.status === 'failed') {
        throw new Error(
          mergedReport.error
          || mergedReport.detail
          || 'The playlist reports could not be combined. Try Combine again.',
        );
      }
      setActiveReport(mergedReport);
      toast.success('Playlist reports combined.');
    } catch (mergeError) {
      const message = mergeError?.message || 'Failed to combine playlist reports.';
      setReportError(message);
      toast.error(message);
    } finally {
      if (reachedTerminalStatus) {
        mergeAttemptRef.current = { rawReportId: '', requestId: '' };
      }
      setMerging(false);
    }
  };

  const running = RUNNING_STATUSES.has(activeReport?.status);
  const complete = activeReport?.status === 'complete';
  const failed = activeReport?.status === 'failed';
  const canContinue = complete
    && activeReport?.playlist_truncated === true
    && activeReport?.is_merged !== true;
  const batchStart = Number(activeReport?.batch_start_position || 0);
  const batchEnd = batchStart + Number(activeReport?.video_count || 0);
  const progressCurrent = Number(activeReport?.progress?.current || 0);
  const progressTotal = Number(activeReport?.progress?.total || 0);
  const progressPercent = progressTotal > 0
    ? Math.min(100, Math.round((progressCurrent / progressTotal) * 100))
    : 8;

  return (
    <div className="space-y-6">
      <Card className="border border-fuchsia-200 bg-gradient-to-br from-[#fff5ff] via-white to-[#f6f1ff] p-6 dark:border-fuchsia-500/15 dark:bg-gradient-to-br dark:from-[#1d1420] dark:via-[#17121b] dark:to-[#12101a]">
        <div className="max-w-3xl">
          <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-fuchsia-700 dark:text-fuchsia-300">
            Playlist Intelligence
          </p>
          <h2 className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">
            Learn from what dads are already hearing without borrowing someone else&apos;s voice
          </h2>
          <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
            Bring a public YouTube playlist. We will trace the real evidence, name what fits Dad Partners,
            and turn the strongest response into a practical next move.
          </p>
        </div>

        <div className="mt-6 grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(220px,0.45fr)_auto]">
          <Input
            aria-label="YouTube playlist URL"
            className="border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:placeholder:text-slate-500"
            onChange={(event) => setPlaylistUrl(event.target.value)}
            placeholder="https://www.youtube.com/playlist?list=..."
            type="url"
            value={playlistUrl}
          />
          <Input
            aria-label="Optional niche label"
            className="border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:placeholder:text-slate-500"
            onChange={(event) => setLabel(event.target.value)}
            placeholder="Optional niche label"
            type="text"
            value={label}
          />
          <Button
            className="bg-fuchsia-600 text-white hover:bg-fuchsia-700 dark:bg-fuchsia-500 dark:text-white dark:hover:bg-fuchsia-400"
            disabled={submitting}
            onClick={handleAnalyze}
            type="button"
          >
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ListVideo className="mr-2 h-4 w-4" />}
            Analyze a playlist
          </Button>
        </div>
      </Card>

      {loadError ? (
        <Card className="border-rose-200 bg-rose-50/80 p-5 dark:border-rose-500/20 dark:bg-rose-500/10">
          <p className="text-sm text-rose-700 dark:text-rose-200">{loadError}</p>
          <Button
            className="mt-3 border-rose-300 text-rose-700 hover:bg-rose-100 dark:border-rose-400/30 dark:text-rose-200 dark:hover:bg-rose-400/10"
            onClick={() => fetchReports()}
            type="button"
            variant="outline"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry reports
          </Button>
        </Card>
      ) : null}

      {reportError ? (
        <Card className="border-amber-200 bg-amber-50/80 p-5 dark:border-amber-500/20 dark:bg-amber-500/10">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-600 dark:text-amber-300" />
            <div>
              <p className="text-sm text-amber-800 dark:text-amber-200">{reportError}</p>
              <Button
                className="mt-3 border-amber-300 text-amber-800 hover:bg-amber-100 dark:border-amber-400/30 dark:text-amber-200 dark:hover:bg-amber-400/10"
                disabled={refreshing}
                onClick={handleManualRefresh}
                type="button"
                variant="outline"
              >
                {refreshing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Refresh status
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      {loadingReports && !activeReport ? (
        <Card className="border-slate-200 bg-white/90 p-6 dark:border-white/10 dark:bg-white/[0.03]">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-fuchsia-500" />
            <div>
              <p className="font-semibold text-slate-900 dark:text-white">Loading playlist reports</p>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                The playlist form stays available while history loads.
              </p>
            </div>
          </div>
        </Card>
      ) : null}

      {!loadingReports && !activeReport && !loadError ? (
        <Card className="border-slate-200 bg-white/90 p-8 text-center dark:border-white/10 dark:bg-white/[0.03]">
          <ListVideo className="mx-auto h-8 w-8 text-fuchsia-500" />
          <p className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
            No playlist reports yet
          </p>
          <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-400">
            Start with one playlist you trust enough to study. The report will keep its evidence, brand conflicts,
            and next steps together for review.
          </p>
        </Card>
      ) : null}

      {running ? (
        <Card className="border-fuchsia-200 bg-fuchsia-50/70 p-6 dark:border-fuchsia-500/20 dark:bg-fuchsia-500/[0.08]">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-fuchsia-600 dark:text-fuchsia-300" />
                <p className="text-lg font-semibold text-slate-900 dark:text-white">
                  {activeReport?.progress?.stage || 'Working through the playlist'}
                </p>
              </div>
              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                This report keeps moving in the background. You can leave this tool and return to the saved run.
              </p>
            </div>
            <Badge className="border-fuchsia-500/20 bg-white text-fuchsia-700 dark:border-fuchsia-400/20 dark:bg-white/[0.06] dark:text-fuchsia-200">
              {progressTotal > 0 ? `${progressCurrent} / ${progressTotal}` : activeReport?.status}
            </Badge>
          </div>
          <div className="mt-5 h-2 overflow-hidden rounded-full bg-fuchsia-100 dark:bg-white/10">
            <div
              className="h-full rounded-full bg-fuchsia-600 transition-all dark:bg-fuchsia-400"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </Card>
      ) : null}

      {failed ? (
        <Card className="border-rose-200 bg-rose-50/80 p-6 dark:border-rose-500/20 dark:bg-rose-500/10">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-rose-600 dark:text-rose-300" />
            <div>
              <p className="font-semibold text-rose-800 dark:text-rose-100">This playlist run failed</p>
              <p className="mt-2 text-sm leading-6 text-rose-700 dark:text-rose-200">
                {activeReport?.error || 'The report stopped before it could finish.'}
              </p>
              <Button
                className="mt-4 border-rose-300 text-rose-700 hover:bg-rose-100 dark:border-rose-400/30 dark:text-rose-200 dark:hover:bg-rose-400/10"
                onClick={handleManualRefresh}
                type="button"
                variant="outline"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry status
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      {complete ? (
        <>
          {canContinue ? (
            <Card className="border-amber-200 bg-amber-50/80 p-5 dark:border-amber-500/20 dark:bg-amber-500/10">
              <p className="text-sm leading-6 text-amber-800 dark:text-amber-100">
                Only videos {batchStart + 1}–{batchEnd} of this playlist were analyzed. Continue with the next batch?
              </p>
              <Button
                className="mt-4 border-amber-300 text-amber-800 hover:bg-amber-100 dark:border-amber-400/30 dark:text-amber-100 dark:hover:bg-amber-400/10"
                disabled={continuing}
                onClick={() => handleAnalyze({ resumeFromReport: activeReport.report_id })}
                type="button"
                variant="outline"
              >
                {continuing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ListVideo className="mr-2 h-4 w-4" />}
                Continue with the next batch
              </Button>
            </Card>
          ) : null}

          {activeReport?.is_merged !== true && rawReportsForActivePlaylist.length >= 2 ? (
            <Card className="border-sky-200 bg-sky-50/80 p-5 dark:border-sky-500/20 dark:bg-sky-500/10">
              <p className="text-sm leading-6 text-sky-800 dark:text-sky-100">
                Combine these completed batches into one playlist report.
              </p>
              <Button
                className="mt-4 border-sky-300 text-sky-800 hover:bg-sky-100 dark:border-sky-400/30 dark:text-sky-100 dark:hover:bg-sky-400/10"
                disabled={merging}
                onClick={handleMerge}
                type="button"
                variant="outline"
              >
                {merging ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                Combine into one report
              </Button>
            </Card>
          ) : null}

          <section className="space-y-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-fuchsia-700 dark:text-fuchsia-300">
                {reportLabel(activeReport)}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Badge className="border-fuchsia-500/20 bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300">
                  {reportTypeLabel(activeReport)}
                </Badge>
                {reportWindowLabel(activeReport) ? (
                  <Badge className="border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300">
                    {reportWindowLabel(activeReport)}
                  </Badge>
                ) : null}
              </div>
              <h2 className="mt-2 text-2xl font-semibold text-slate-900 dark:text-white">
                Top 3 Recommended
              </h2>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                Highest-impact moves that cleared the Dad Partners brand gate.
              </p>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                {activeReport?.is_merged === true
                  ? 'This report combines the completed playlist batches into one saved synthesis.'
                  : 'This saved report covers one playlist batch; continue only when the playlist still needs another window.'}
              </p>
            </div>
            <div className="grid gap-4 xl:grid-cols-3">
              {topIdeas.map((idea, index) => (
                <IdeaCard
                  hero
                  idea={idea}
                  key={idea.idea_id}
                  onAddToQueue={handleAddToQueue}
                  onCopyBrief={handleCopyBrief}
                  rank={index + 1}
                />
              ))}
            </div>
          </section>

          <Card className="border-slate-200 bg-white/90 p-6 dark:border-white/10 dark:bg-white/[0.03]">
            <button
              className="flex w-full items-center justify-between text-left"
              onClick={() => setAllIdeasOpen((current) => !current)}
              type="button"
            >
              <div>
                <p className="text-lg font-semibold text-slate-900 dark:text-white">All ideas</p>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  Review the full ranked set, including tactics held back by the brand gate.
                </p>
              </div>
              {allIdeasOpen ? <ChevronUp className="h-5 w-5 text-slate-500 dark:text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-500 dark:text-slate-400" />}
            </button>
            {allIdeasOpen ? (
              <div className="mt-6 space-y-4">
                {ideas.map((idea, index) => (
                  <IdeaCard
                    idea={idea}
                    key={idea.idea_id}
                    onAddToQueue={handleAddToQueue}
                    onCopyBrief={handleCopyBrief}
                    rank={index + 1}
                  />
                ))}
              </div>
            ) : null}
          </Card>

          <Card className="border-violet-200 bg-violet-50/60 p-6 dark:border-violet-500/20 dark:bg-violet-500/[0.07]">
            <button
              className="flex w-full items-center justify-between text-left"
              onClick={() => setContraryOpen((current) => !current)}
              type="button"
            >
              <div>
                <p className="text-lg font-semibold text-slate-900 dark:text-white">
                  Contrary beliefs in this niche
                </p>
                <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                  These beliefs fail the strategy gate, but answering them with care can become some of the strongest content.
                </p>
              </div>
              {contraryOpen ? <ChevronUp className="h-5 w-5 text-violet-600 dark:text-violet-300" /> : <ChevronDown className="h-5 w-5 text-violet-600 dark:text-violet-300" />}
            </button>

            {contraryOpen ? (
              <div className="mt-6 space-y-4">
                {flaggedIdeas.length ? flaggedIdeas.map((idea) => {
                  const counter = idea.counter_angle;
                  const promoted = ideas.find(
                    (candidate) => candidate.derived_from_idea_id === idea.idea_id,
                  );
                  return (
                    <div
                      className="rounded-2xl border border-violet-200 bg-white/90 p-5 dark:border-violet-400/20 dark:bg-white/[0.04]"
                      key={idea.idea_id}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className="border-rose-500/20 bg-rose-500/10 text-rose-700 dark:text-rose-300">
                          Not strategy
                        </Badge>
                        {promoted ? (
                          <Badge className="border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
                            Also ranked above
                          </Badge>
                        ) : null}
                      </div>
                      <div className="mt-5 grid gap-4 xl:grid-cols-2">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                            The belief
                          </p>
                          <p className="mt-2 text-lg font-semibold leading-8 text-slate-900 dark:text-white">
                            {counter.contrary_belief}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                            Why it appeals
                          </p>
                          <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-300">
                            {counter.why_it_appeals}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                            Where it breaks
                          </p>
                          <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-300">
                            {counter.where_it_breaks}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-fuchsia-700 dark:text-fuchsia-300">
                            Dad Partners response
                          </p>
                          <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-300">
                            {counter.dad_partners_response}
                          </p>
                        </div>
                      </div>
                      <div className="mt-5 rounded-2xl border border-fuchsia-200 bg-fuchsia-50/70 p-4 dark:border-fuchsia-500/20 dark:bg-fuchsia-500/[0.08]">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-fuchsia-700 dark:text-fuchsia-300">
                          Working YouTube title
                        </p>
                        <p className="mt-2 font-semibold text-slate-900 dark:text-white">
                          {counter.content_title}
                        </p>
                      </div>
                      <EvidenceList evidence={idea.evidence} />
                      <div className="mt-5 flex flex-wrap gap-2">
                        {promoted?.lens === 'content' ? (
                          <Button
                            className="bg-fuchsia-600 text-white hover:bg-fuchsia-700 dark:bg-fuchsia-500 dark:text-white dark:hover:bg-fuchsia-400"
                            onClick={() => handleAddToQueue(promoted.title || counter.content_title)}
                            type="button"
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Add response to Queue
                          </Button>
                        ) : null}
                        <Button
                          className="border-slate-200 text-slate-700 hover:bg-slate-100 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/[0.06]"
                          onClick={() => handleCopyBrief(promoted || idea)}
                          type="button"
                          variant="outline"
                        >
                          <Clipboard className="mr-2 h-4 w-4" />
                          Copy brief
                        </Button>
                      </div>
                    </div>
                  );
                }) : (
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    This playlist did not surface a contrary belief that needed the brand gate.
                  </p>
                )}
              </div>
            ) : null}
          </Card>

          <Card className="border-slate-200 bg-white/90 p-6 dark:border-white/10 dark:bg-white/[0.03]">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-300" />
              <p className="text-lg font-semibold text-slate-900 dark:text-white">Playlist map</p>
            </div>
            <p className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50/80 p-3 text-sm leading-6 text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200">
              {evidenceMixText(activeReport)}
            </p>
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {asArray(activeReport?.themes).map((theme) => (
                <div
                  className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 dark:border-white/10 dark:bg-white/[0.04]"
                  key={theme.label}
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-semibold text-slate-900 dark:text-white">{theme.label}</p>
                    <Badge className="border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300">
                      {asArray(theme.video_ids).length} videos
                    </Badge>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
                    {theme.summary}
                  </p>
                </div>
              ))}
            </div>
          </Card>
        </>
      ) : null}

      {reports.length ? (
        <Card className="border-slate-200 bg-white/90 p-6 dark:border-white/10 dark:bg-white/[0.03]">
          <button
            className="flex w-full items-center justify-between text-left"
            onClick={() => setHistoryOpen((current) => !current)}
            type="button"
          >
            <div>
              <p className="text-lg font-semibold text-slate-900 dark:text-white">Past reports</p>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                Reopen a saved playlist read without rerunning the analysis.
              </p>
            </div>
            {historyOpen ? <ChevronUp className="h-5 w-5 text-slate-500 dark:text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-500 dark:text-slate-400" />}
          </button>
          {historyOpen ? (
            <div className="mt-5 space-y-5">
              {groupedHistoryReports.map((group) => (
                <section key={group.key}>
                  <h3 className="font-semibold text-slate-900 dark:text-white">{group.title}</h3>
                  <div className="mt-2 space-y-2">
                    {group.reports.map((report) => {
                      const batchIndex = Number(report?.batch_index);
                      const batchNumber = Number.isInteger(batchIndex) && batchIndex >= 0 ? batchIndex + 1 : 1;
                      const reportType = report?.is_merged === true ? 'Combined report' : `Batch ${batchNumber}`;
                      const reportTypeClassName = report?.is_merged === true
                        ? 'font-semibold text-fuchsia-700 dark:text-fuchsia-300'
                        : 'font-semibold text-slate-900 dark:text-white';
                      return (
                        <button
                          className="flex w-full items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50/80 p-4 text-left transition hover:border-fuchsia-300 hover:bg-fuchsia-50/60 dark:border-white/10 dark:bg-white/[0.04] dark:hover:border-fuchsia-400/30 dark:hover:bg-fuchsia-400/[0.08]"
                          key={report.report_id}
                          onClick={() => handleOpenReport(report.report_id)}
                          type="button"
                        >
                          <div>
                            <p className={reportTypeClassName}>{reportType}</p>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                              {formatDateTime(report.created_at)} · {Number(report.video_count || 0)} videos
                            </p>
                          </div>
                          <Badge className="border-slate-500/20 bg-white text-slate-700 dark:border-white/10 dark:bg-white/[0.05] dark:text-slate-300">
                            {report.status}
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}
